import { useCallback, useEffect, useState } from 'react'
import * as moment from 'moment'
import FieldPathHelper from '@/util/FieldPathHelper'

export function useSecureShare({ kedo, contentDossier }) {
    const [state, setState] = useState({
        loading: false,
        processing: false,
        error: false,
        shareType: 'positions',
        shareUntilDate: moment().add(7, 'days').format('DD-MM-YYYY'),
        shareTypeValue: null,
        step: 1,
        contacts: [],
        kedocxs: [],
        displayPositions: [],
        hasKedocxs: null,
        hasDisplayPositions: null,
        recipientUsers: [],
        dossierPersons: [],
        recipientDossierPersons: [],
        recipientCustomEmails: [],
        recipientCustomEmailsInput: '',
        secureShareSettings: null,
    })

    const setPartialState = useCallback((updates) => {
        setState((prev) => ({ ...prev, ...updates }))
    }, [])

    const fetchSecureShare = useCallback(
        async (modules) => {
            const api = kedo.api()
            try {
                for (const module of modules) {
                    if (module.module === 'secureshare') {
                        const response = await api.get(
                            `${api.getEnvironmentModuleEndpoint()}/${module.id}`
                        )
                        const settings = response.data.settings.entities.find(
                            (item) =>
                                parseInt(item.def_dossier_id) ===
                                contentDossier.def_dossier_id
                        )

                        if (!settings) return

                        setPartialState({ secureShareSettings: settings })

                        const contactIds = await FieldPathHelper.getFieldData(
                            contentDossier?.id,
                            settings?.path_field
                        )

                        if (Array.isArray(contactIds)) {
                            const persons = await Promise.all(
                                contactIds.map(async (contactId) => {
                                    const [name, email, mobile] =
                                        await Promise.all([
                                            FieldPathHelper.getFieldData(
                                                contactId,
                                                settings?.name_field
                                            ),
                                            FieldPathHelper.getFieldData(
                                                contactId,
                                                settings?.email_field
                                            ),
                                            FieldPathHelper.getFieldData(
                                                contactId,
                                                settings?.sms_field
                                            ),
                                        ])
                                    return email
                                        ? { id: contactId, name, email, mobile }
                                        : null
                                })
                            )

                            setPartialState({
                                dossierPersons: persons.filter(Boolean),
                            })
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching secure share settings:', err)
            }
        },
        [contentDossier, kedo, setPartialState]
    )

    const fetchModule = useCallback(async () => {
        const api = kedo.api()
        setPartialState({ loading: true })

        try {
            const filters = { limit: 10, page: 0 }
            const moduleRes = await api.get(api.getModuleEndpoint(), {
                params: filters,
            })
            const envModuleNames = moduleRes.data.results
                .map((item) => item.name)
                .join(',')

            const envModuleParams = {
                params: {
                    names: envModuleNames,
                    limit: 100,
                    environment: kedo.env().getEnvironment().id,
                },
            }

            const envModuleRes = await api.get(
                api.getEnvironmentModuleEndpoint(),
                envModuleParams
            )
            const module = envModuleRes.data.results
            setPartialState({ loading: false })
            fetchSecureShare(module)
        } catch (err) {
            console.error(err)
            setPartialState({ loading: false })
        }
    }, [fetchSecureShare, kedo, setPartialState])

    const fetchKedocx = useCallback(async () => {
        const api = kedo.api()
        const params = {
            defDossier: contentDossier.def_dossier_id,
            limit: 300,
        }

        try {
            const response = await api.get(api.getKedocxEndpoint(), { params })
            const hasItems = response.data.pager.items > 0
            setPartialState({
                kedocxs: response.data.results,
                hasKedocxs: hasItems,
                shareType: !hasItems ? 'positions' : state.shareType,
            })
        } catch (err) {
            console.error(err)
        }
    }, [kedo, contentDossier, state.shareType, setPartialState])

    const fetchPositions = useCallback(async () => {
        const api = kedo.api()
        const params = {
            defDossier: contentDossier.def_dossier_id,
            limit: 300,
        }

        try {
            const response = await api.get(api.getDisplayPositionEndpoint(), {
                params,
            })
            const positions = response.data.results
            const mainPosition = positions.find(
                (dpItem) => dpItem.type === 'main_content'
            )

            setPartialState({
                displayPositions: positions,
                hasDisplayPositions: response.data.pager.items > 0,
                shareTypeValue: mainPosition?.id || state.shareTypeValue,
            })
        } catch (err) {
            console.error(err)
        }
    }, [kedo, contentDossier, state.shareTypeValue, setPartialState])

    useEffect(() => {
        fetchModule()
        fetchKedocx()
        fetchPositions()
    }, [fetchModule, fetchKedocx, fetchPositions])

    return {
        state,
        setState,
        setPartialState,
        fetchModule,
        fetchKedocx,
        fetchPositions,
        fetchSecureShare,
    }
}
